import { Alert, Autocomplete, Box, Button, CircularProgress, MenuItem, TextField, Typography } from '@mui/material';
import { Formik, FormikProps, Field } from 'formik';
import { WorkspaceField, defaultCreateWorkspace } from '../../components/WorkspaceField';
import * as Yup from 'yup';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { FormikTextField, FormikSwitch } from '../../components/FormikFields';
import FormDirtyTracker from '../../components/FormDirtyTracker';
import TagSelect from '../../components/TagSelect';
import { networksService, Network } from '../../services/networks.service';
import { TIMEZONE_OPTIONS } from '../../utils/timezone';
import WorkingHoursField, { defaultWorkingHours, scheduleIsValid, WorkingHours } from './WorkingHoursField';

const capField = () => Yup.number()
  .transform((v, raw) => (raw === '' || raw === null ? 0 : v))
  .integer('Must be a whole number')
  .min(0, 'Cannot be negative')
  .default(0);

const schema = Yup.object({
  name: Yup.string().trim().required('Name is required'),
  click_id_param: Yup.string().trim().default('click_id'),
  currency_code: Yup.string().trim().default('USD'),
  report_to_source: Yup.boolean().default(false),
  use_redirect_page: Yup.boolean().default(false),
  referrer_policy: Yup.string().oneOf(['none', 'origin', 'full']).default('origin'),
  tag_ids: Yup.array().of(Yup.string()).default([]),
  working_hours_enabled: Yup.boolean().default(false),
  timezone: Yup.number().min(-12).max(14).default(0),
  daily_spend_cap: capField(),
  daily_conversion_cap: capField(),
  daily_revenue_cap: capField(),
});

interface NetworkFormValues {
  name: string;
  kind: string;
  notes: string;
  ref: string;
  workspace_id: string;
  click_id_param: string;
  currency_code: string;
  report_to_source: boolean;
  use_redirect_page: boolean;
  referrer_policy: 'none' | 'origin' | 'full';
  tag_ids: string[];
  working_hours_enabled: boolean;
  working_hours: WorkingHours | null;
  timezone: number;
  daily_spend_cap: number;
  daily_conversion_cap: number;
  daily_revenue_cap: number;
}

function toInitialValues(network?: Network, duplicate = false): NetworkFormValues {
  if (!network) {
    return {
      name: '', kind: '', notes: '', ref: '', workspace_id: defaultCreateWorkspace(),
      click_id_param: 'click_id', currency_code: 'USD', report_to_source: false,
      use_redirect_page: false, referrer_policy: 'origin', tag_ids: [],
      working_hours_enabled: false, working_hours: null, timezone: 0,
      daily_spend_cap: 0, daily_conversion_cap: 0, daily_revenue_cap: 0,
    };
  }
  return {
    name: duplicate ? `${network.name} (copy)` : network.name,
    kind: network.kind ?? '',
    notes: network.notes ?? '',
    ref: duplicate ? '' : (network.ref ?? ''),
    workspace_id: duplicate ? defaultCreateWorkspace() : (network as any).workspace_id ?? defaultCreateWorkspace(),
    click_id_param: network.click_id_param || 'click_id',
    currency_code: network.currency_code || 'USD',
    report_to_source: network.report_to_source,
    use_redirect_page: network.use_redirect_page ?? false,
    referrer_policy: network.referrer_policy || 'origin',
    tag_ids: network.tag_ids ?? [],
    working_hours_enabled: !!network.working_hours,
    working_hours: (network.working_hours ?? null) as WorkingHours | null,
    timezone: Number(network.timezone ?? 0),
    daily_spend_cap: Number(network.daily_spend_cap ?? 0),
    daily_conversion_cap: Number(network.daily_conversion_cap ?? 0),
    daily_revenue_cap: Number(network.daily_revenue_cap ?? 0),
  };
}

export default function NetworkForm({ network, onSaved, duplicate = false, onDirtyChange }: {
  network?: Network; onSaved?: (n: Network) => void; duplicate?: boolean;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = !!network && !duplicate;
  const { data: kindOptions = [] } = useQuery({ queryKey: ['network-kinds'], queryFn: networksService.getKinds });

  return (
    <Formik
      initialValues={toInitialValues(network, duplicate)}
      enableReinitialize
      validationSchema={schema}
      onSubmit={async (values, { setSubmitting, setStatus }) => {
        try {
          const { working_hours_enabled, working_hours, ...rest } = values;
          const payload = {
            ...rest,
            timezone: Number(rest.timezone) || 0,
            daily_spend_cap: Number(rest.daily_spend_cap) || 0,
            daily_conversion_cap: Number(rest.daily_conversion_cap) || 0,
            daily_revenue_cap: Number(rest.daily_revenue_cap) || 0,
            working_hours: working_hours_enabled ? (working_hours ?? defaultWorkingHours()) : null,
          };
          const saved = isEdit
            ? await networksService.update(network!.id, payload)
            : await networksService.create(payload);
          queryClient.invalidateQueries({ queryKey: ['networks'] });
          if (isEdit) queryClient.invalidateQueries({ queryKey: ['network', network!.id] });
          enqueueSnackbar('Saved', { variant: 'success' });
          onSaved?.(saved);
        } catch (err: any) {
          setStatus(err?.response?.data?.error?.message || err.message || 'Save failed');
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ submitForm, isSubmitting, status, values, setFieldValue }: FormikProps<NetworkFormValues>) => (
        <Box
          component="form"
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' },
            columnGap: 2, rowGap: 2, alignItems: 'start', maxWidth: 1100,
          }}
        >
          {onDirtyChange && <FormDirtyTracker onDirtyChange={onDirtyChange} />}
          <Box sx={{ gridColumn: '1 / -1', display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            <FormikTextField name="name" label="Name" size="small" sx={{ flex: 1 }} />
            <FormikTextField name="ref" label="Ref" size="small" placeholder="e.g. N1200" sx={{ width: { xs: '100%', sm: 160 } }} />
          </Box>
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Autocomplete
              freeSolo fullWidth options={kindOptions}
              value={values.kind || null}
              onChange={(_, v) => setFieldValue('kind', v ?? '')}
              inputValue={values.kind}
              onInputChange={(_, v) => setFieldValue('kind', v)}
              renderInput={(params) => (
                <TextField {...params} label="Kind (optional)" size="small"
                  placeholder="e.g. Taboola — groups this network with its copies in other workspaces" />
              )}
            />
          </Box>
          {!isEdit && (
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Field name="workspace_id">
                {({ field, form }: any) => (
                  <WorkspaceField value={field.value} onChange={(id) => {
                    // Tags live in the source workspace — they can't follow to another one.
                    if (id !== field.value) form.setFieldValue('tag_ids', []);
                    form.setFieldValue('workspace_id', id);
                  }} />
                )}
              </Field>
            </Box>
          )}
          <FormikTextField
            name="notes" label="Notes" fullWidth size="small" multiline rows={2}
            sx={{ gridColumn: '1 / -1' }}
          />
          <FormikTextField
            name="click_id_param" label="Click ID Parameter" fullWidth size="small"
            helperText="Parameter name used to pass click ID to network (e.g. click_id, yk_tag, aff_sub)"
          />
          <FormikTextField
            name="currency_code" label="Currency Code" fullWidth size="small"
            helperText="ISO currency code (e.g. USD, EUR, GBP)"
          />
          <Autocomplete
            size="small" fullWidth
            options={[
              { value: 'none', label: 'None (no referrer)' },
              { value: 'origin', label: 'Origin (recommended)' },
              { value: 'full', label: 'Full (complete URL)' },
            ]}
            getOptionLabel={(o) => o.label}
            value={{ value: values.referrer_policy, label: values.referrer_policy === 'none' ? 'None (no referrer)' : values.referrer_policy === 'full' ? 'Full (complete URL)' : 'Origin (recommended)' }}
            onChange={(_, v) => setFieldValue('referrer_policy', v?.value || 'origin')}
            disableClearable
            isOptionEqualToValue={(o, v) => o.value === v.value}
            renderInput={(params) => <TextField {...params} label="Referrer Policy" size="small" />}
          />
          <TagSelect value={values.tag_ids} onChange={(ids) => setFieldValue('tag_ids', ids)} />
          <Box sx={{ gridColumn: '1 / -1' }}>
            <FormikSwitch name="report_to_source" label="Report conversions to traffic source (FB CAPI)" />
          </Box>
          <Box sx={{ gridColumn: '1 / -1' }}>
            <FormikSwitch name="use_redirect_page" label="Serve 200 redirect page" />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Serve a 200 HTML page that redirects to the offer (so our domain becomes the referer), instead of a bare 302.
            </Typography>
          </Box>
          <Typography variant="overline" color="text.secondary" sx={{ gridColumn: '1 / -1' }}>
            Traffic control
          </Typography>
          <TextField
            select size="small" label="Timezone" value={values.timezone} fullWidth
            onChange={(e) => setFieldValue('timezone', Number(e.target.value))}
            helperText="Clock for the daily caps and working hours below. Fixed UTC offset — no DST."
          >
            {TIMEZONE_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </TextField>
          <FormikTextField
            name="daily_spend_cap" label="Daily spend cap" type="number" fullWidth size="small"
            helperText="0 = no cap. Measured from reported spend, so leave headroom."
          />
          <FormikTextField
            name="daily_conversion_cap" label="Daily conversion cap" type="number" fullWidth size="small"
            helperText="0 = no cap. Max conversions per day before the network's offers are skipped."
          />
          <FormikTextField
            name="daily_revenue_cap" label="Daily revenue cap" type="number" fullWidth size="small"
            helperText="0 = no cap. Whole units, summed as the revenue report does (not currency-converted)."
          />
          <Box sx={{ gridColumn: '1 / -1' }}>
            <WorkingHoursField
              value={values.working_hours_enabled ? (values.working_hours ?? defaultWorkingHours()) : null}
              onChange={(v) => setFieldValue('working_hours', v)}
              onNormalise={(v) => setFieldValue('working_hours', v)}
            />
          </Box>
          {status && <Alert severity="error" sx={{ gridColumn: '1 / -1' }}>{status}</Alert>}
          <Button
            variant="contained" onClick={submitForm}
            sx={{ gridColumn: '1 / -1', justifySelf: 'flex-start' }}
            disabled={isSubmitting || !values.workspace_id || (values.working_hours_enabled && !scheduleIsValid(values.working_hours))}
          >
            {isSubmitting ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </Box>
      )}
    </Formik>
  );
}
